import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ConfigInfo, IRoundRobinData } from "../../models/roundRobin.model";
import {
  startCronJobApi,
  getCronJobStatusApi,
  getRoundRobinDataApi,
  updateRoundRobinDataApi,
  stopCronJobApi,
  storeRoundRobinDataApi,
  resumeCronJobApi,
  pauseCronJobApi,
  getAllTimeWhatsAppAutomationReportApi,
  getReportOfAllNumbersBasedOnDateApi,
  getReportBasedOnDateApi,
  clearRoundRobinHeaderFooterBodyApi,
} from "../../api/roundRobinData.api";
import {
  deleteAllObjectsFromS3BucketApi,
  uploadToS3ForCronJobApi,
} from "../../api/s3.api";

const initialState: IRoundRobinData & { hasDataInDB: boolean } = {
  createdAt: "",
  updatedAt: "",
  cronJobStatus: null,
  error: null,
  hasDataInDB: false,
  configInfo: {
    cap: 0,
    numberOfTimesToSendMsg: 0,
    numberOfDaysBeforeReset: 0,
    AlertMobileNumbers: [],
    template: "",
    messageSentCount: 0,
    noOfDaysCount: 0,
    delay: 0,
    isPaused: false,
    headers: [],
    footer: [],
    body: [],
  },
  loadingStates: {
    fetchData: false,
    updateData: false,
    startJob: false,
    stopJob: false,
    uploadFile: false,
    getStatus: false,
    pauseJob: false,
    resumeJob: false,
    deleteFiles: false,
  },
  report: {
    totalSent: 0,
    totalSuccess: 0,
    totalFailed: 0,
    totalLeads: 0,
    totalRead: 0,
  },
  reportForToday: {
    totalSent: 0,
    totalSuccess: 0,
    totalFailed: 0,
    totalLeads: 0,
    totalRead: 0,
  },
  isPaused: false,
  NumberWiseReport: [],
  selectedDate: new Date().toISOString().split("T")[0],
  selectDateForPerformance: new Date().toISOString().split("T")[0],
};

// Async Thunks
export const getRoundRobinData = createAsyncThunk(
  "getRoundRobinData",
  async () => {
    const response = await getRoundRobinDataApi();
    return response;
  },
);
export const getReportOfAllNumbersBasedOnDate = createAsyncThunk(
  "getReportOfAllNumbersBasedOnDate",
  async (date: string) => {
    const response = await getReportOfAllNumbersBasedOnDateApi(date);
    return response;
  },
);
export const getReportBasedOnDate = createAsyncThunk(
  "getReportBasedOnDate",
  async (date: string) => {
    const response = await getReportBasedOnDateApi(date);
    return response;
  },
);
export const getWhatsAppAutomationReport = createAsyncThunk(
  "getWhatsAppAutomationReport",
  async () => {
    const response = await getAllTimeWhatsAppAutomationReportApi();
    return response;
  },
);

export const storeRoundRobinData = createAsyncThunk(
  "storeRoundRobinData",
  async (roundRobinData: ConfigInfo) => {
    const response = await storeRoundRobinDataApi(roundRobinData);
    return response;
  },
);

export const updateRoundRobinData = createAsyncThunk(
  "updateRoundRobinData",
  async (data: ConfigInfo) => {
    const response = await updateRoundRobinDataApi(data);
    return response;
  },
);

export const startCronJobBasedOnRoundRobinData = createAsyncThunk(
  "shootwhatsappMessageBasedOnRoundRobinData",
  async () => {
    const response = await startCronJobApi();
    return response;
  },
);

export const stopCronJob = createAsyncThunk("stopCronJob", async () => {
  const response = await stopCronJobApi();
  return response;
});
export const pauseCronJob = createAsyncThunk("pauseCronJob", async () => {
  const response = await pauseCronJobApi();
  return response;
});

export const resumeCronJob = createAsyncThunk("resumeCronJob", async () => {
  const response = await resumeCronJobApi();
  return response;
});

export const getCronJobStatus = createAsyncThunk(
  "getCronJobStatus",
  async () => {
    const response = await getCronJobStatusApi();
    return response;
  },
);

export const uploadtoS3 = createAsyncThunk(
  "uploadtoS3",
  async (file: FormData) => {
    const response = await uploadToS3ForCronJobApi(file);
    return response;
  },
);

export const deleteAllObjectsFromS3Bucket = createAsyncThunk(
  "deleteAllObjectsFromS3Bucket",
  async (bucketName: string) => {
    const response = await deleteAllObjectsFromS3BucketApi(bucketName);
    return response;
  },
);

export const clearRoundRobinHeaderFooterBody = createAsyncThunk(
  "clearRoundRobinHeaderFooterBody",
  async () => {
    const response = await clearRoundRobinHeaderFooterBodyApi();
    return response;
  },
);

// Slice
const roundRobinSlice = createSlice({
  name: "roundRobin",
  initialState,
  reducers: {
    setIsPaused: (state, action) => {
      state.isPaused = action.payload;
    },

    updateConfigInfo: (state, action) => {
      state.configInfo = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectDateForPerformance: (state, action) => {
      state.selectDateForPerformance = action.payload;
    },
    updateTodayReportSummary: (state, action) => {
      state.reportForToday!.totalSent = action.payload.totalSent;
      state.reportForToday!.totalSuccess = action.payload.totalSuccess;
      state.reportForToday!.totalFailed = action.payload.totalFailed;
      state.reportForToday!.totalLeads = action.payload.totalLeads;
      state.reportForToday!.totalRead = action.payload.totalRead;
    },
    addReportOfEachNumber: (state, action) => {
      const newReport = action.payload;

      // Find existing report with same messageId and mobileNumber
      const existingIndex = state.NumberWiseReport!.findIndex(
        (report) =>
          report.messageId === newReport.messageId &&
          report.mobileNumber === newReport.mobileNumber,
      );

      if (existingIndex !== -1) {
        // Update existing report
        state.NumberWiseReport![existingIndex] = {
          ...newReport,
        };
      } else {
        // Add new report only if it doesn't exist
        state.NumberWiseReport?.push(newReport);
      }
    },
    updateReportSummary: (state, action) => {
      state.report!.totalSent = action.payload.totalSent;
      state.report!.totalSuccess = action.payload.totalSuccess;
      state.report!.totalFailed = action.payload.totalFailed;
      state.report!.totalRead = action.payload.totalRead;
    },
    clearFileNameAndUrl: (state) => {
      state.configInfo.documentFilename = "";
      state.configInfo.documentUrl = "";
    },
    isRoundRobinDataAvailable: (state) => {
      if (
        state.configInfo._id === "" &&
        state.configInfo.cap === 0 &&
        state.configInfo.template === "" &&
        state.configInfo.numberOfDaysBeforeReset === 0 &&
        state.configInfo.numberOfTimesToSendMsg === 0 &&
        state.configInfo?.AlertMobileNumbers?.length === 0 &&
        state.configInfo.messageSentCount === 0 &&
        state.configInfo.noOfDaysCount === 0
      ) {
        state.hasDataInDB = false;
      } else {
        state.hasDataInDB = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportOfAllNumbersBasedOnDate.pending, (state) => {
        state.loadingStates.fetchData = true;
        state.error = null;
      })
      .addCase(getReportBasedOnDate.pending, (state) => {
        state.loadingStates.fetchData = true;
        state.error = null;
      })
      .addCase(getReportBasedOnDate.fulfilled, (state, action) => {
        state.loadingStates.fetchData = false;
        state.reportForToday = action.payload.data;
        state.error = null;
      })
      .addCase(getReportBasedOnDate.rejected, (state, action) => {
        state.loadingStates.fetchData = false;
        state.error = action.error.message || "Failed to fetch today's report.";
      })
      .addCase(getReportOfAllNumbersBasedOnDate.fulfilled, (state, action) => {
        state.loadingStates.fetchData = false;
        state.NumberWiseReport = action.payload.data;
        state.error = null;
      })
      .addCase(getReportOfAllNumbersBasedOnDate.rejected, (state, action) => {
        state.loadingStates.fetchData = false;
        state.error =
          action.error.message || "Failed to fetch report of all numbers.";
      })
      .addCase(getWhatsAppAutomationReport.pending, (state) => {
        state.loadingStates.fetchData = true;
        state.error = null;
      })
      .addCase(getWhatsAppAutomationReport.fulfilled, (state, action) => {
        state.loadingStates.fetchData = false;
        state.report = action.payload.data;
        state.error = null;
      })
      .addCase(getRoundRobinData.pending, (state) => {
        state.loadingStates.fetchData = true;
        state.error = null;
      })
      .addCase(getRoundRobinData.fulfilled, (state, action) => {
        const data = action.payload.data;

        state.configInfo = data;
        state.isPaused = data?.isPaused;
        state.loadingStates.fetchData = false;
        state.error = null;
        state.hasDataInDB = !!data?._id;
        Object.assign(state, data);
      })
      .addCase(getRoundRobinData.rejected, (state, action) => {
        state.loadingStates.fetchData = false;
        state.hasDataInDB = false;
        state.error =
          action.error.message || "Failed to fetch round robin data.";
      })

      .addCase(storeRoundRobinData.pending, (state) => {
        state.loadingStates.updateData = true;
        state.error = null;
      })
      .addCase(storeRoundRobinData.fulfilled, (state, action) => {
        state.loadingStates.updateData = false;
        state.error = null;
        state.hasDataInDB = true;
      })
      .addCase(storeRoundRobinData.rejected, (state, action) => {
        state.loadingStates.updateData = false;
        state.hasDataInDB = false;
        state.error =
          action.error.message || "Failed to store round robin data.";
      })

      .addCase(updateRoundRobinData.pending, (state) => {
        state.loadingStates.updateData = true;
        state.error = null;
      })
      .addCase(updateRoundRobinData.fulfilled, (state, action) => {
        const data = action.payload.data;
        console.log("Updated Data:", data);
        state.loadingStates.updateData = false;
        state.error = null;
        state.hasDataInDB = !!data?._id;
        state.configInfo = data;
        Object.assign(state, data);
      })
      .addCase(updateRoundRobinData.rejected, (state, action) => {
        state.loadingStates.updateData = false;
        state.error =
          action.error.message || "Failed to update round robin data.";
      })

      .addCase(startCronJobBasedOnRoundRobinData.pending, (state) => {
        state.loadingStates.startJob = true;
      })
      .addCase(startCronJobBasedOnRoundRobinData.fulfilled, (state, action) => {
        state.loadingStates.startJob = false;
        state.cronJobStatus = action.payload.cronJobStatus;
        state.error = null;
      })
      .addCase(startCronJobBasedOnRoundRobinData.rejected, (state, action) => {
        state.loadingStates.startJob = false;
        state.error = action.error.message || "Failed to start cron job.";
      })
      .addCase(pauseCronJob.pending, (state) => {
        state.loadingStates.pauseJob = true;
      })
      .addCase(pauseCronJob.fulfilled, (state, action) => {
        state.loadingStates.pauseJob = false;
        state.isPaused = true;
        state.error = null;
      })
      .addCase(pauseCronJob.rejected, (state, action) => {
        state.loadingStates.pauseJob = false;
        state.error = action.error.message || "Failed to pause cron job.";
      })

      .addCase(resumeCronJob.pending, (state) => {
        state.loadingStates.resumeJob = true;
      })
      .addCase(resumeCronJob.fulfilled, (state, action) => {
        state.loadingStates.resumeJob = false;
        state.isPaused = false;
        state.cronJobStatus = true;
        state.error = null;
      })
      .addCase(resumeCronJob.rejected, (state, action) => {
        state.loadingStates.resumeJob = false;
        state.error = action.error.message || "Failed to resume cron job.";
      })

      .addCase(getCronJobStatus.pending, (state) => {
        state.loadingStates.getStatus = true;
        state.error = null;
      })
      .addCase(getCronJobStatus.fulfilled, (state, action) => {
        state.loadingStates.getStatus = false;
        state.cronJobStatus = action.payload;
        state.error = null;
      })
      .addCase(getCronJobStatus.rejected, (state, action) => {
        state.loadingStates.getStatus = false;
        state.cronJobStatus = null;
        state.error =
          action.error.message || "Failed to fetch cron job status.";
      })

      .addCase(stopCronJob.pending, (state) => {
        state.loadingStates.stopJob = true;
        state.error = null;
      })
      .addCase(stopCronJob.fulfilled, (state, action) => {
        state.loadingStates.stopJob = false;
        state.cronJobStatus = action.payload;
        state.error = null;
      })
      .addCase(stopCronJob.rejected, (state, action) => {
        state.loadingStates.stopJob = false;
        state.error = action.error.message || "Failed to stop cron job.";
      })

      .addCase(uploadtoS3.pending, (state) => {
        state.loadingStates.uploadFile = true;
        state.error = null;
      })
      .addCase(uploadtoS3.fulfilled, (state, action) => {
        state.loadingStates.uploadFile = false;
        state.configInfo.documentFilename = action.payload.fileName;
        state.configInfo.documentUrl = action.payload.s3Location;
        state.error = null;
      })
      .addCase(uploadtoS3.rejected, (state, action) => {
        state.loadingStates.uploadFile = false;
        state.error = action.error.message || "Failed to upload file.";
      })
      .addCase(deleteAllObjectsFromS3Bucket.pending, (state) => {
        state.loadingStates.deleteFiles = true;
      })
      .addCase(deleteAllObjectsFromS3Bucket.fulfilled, (state, action) => {
        state.loadingStates.deleteFiles = false;
        state.error = null;
      })
      .addCase(deleteAllObjectsFromS3Bucket.rejected, (state, action) => {
        state.loadingStates.deleteFiles = false;
        state.error = action.error.message || "Failed to delete files.";
      })
      .addCase(clearRoundRobinHeaderFooterBody.pending, (state) => {
        state.loadingStates.updateData = true;
        state.error = null;
      })
      .addCase(clearRoundRobinHeaderFooterBody.fulfilled, (state) => {
        state.loadingStates.updateData = false;
        state.configInfo.headers = [];
        state.configInfo.footer = [];
        state.configInfo.body = [];
        state.error = null;
      })
      .addCase(clearRoundRobinHeaderFooterBody.rejected, (state, action) => {
        state.loadingStates.updateData = false;
        state.error =
          action.error.message ||
          "Failed to clear round robin header/footer/body.";
      });
  },
});

export const {
  isRoundRobinDataAvailable,
  clearFileNameAndUrl,
  updateReportSummary,
  setIsPaused,
  addReportOfEachNumber,
  setSelectDateForPerformance,
  setSelectedDate,
  updateTodayReportSummary,
  updateConfigInfo,
  // updateTemplateConfig,
} = roundRobinSlice.actions;

export default roundRobinSlice.reducer;
